/**
 * WebSocket 管理类
 * 实现了单例模式、自动重连、命令发送、轮询等功能
 */
export default class WebSocketManager {
  static instance = null;

  /**
   * 获取 WebSocketManager 实例
   * @param {string} webSocketURL - WebSocket 连接地址
   * @param {Object} [options={}] - 配置选项
   * @returns {WebSocketManager}
   */
  static getInstance(webSocketURL, options = {}) {
    if (!WebSocketManager.instance) {
      WebSocketManager.instance = new WebSocketManager(webSocketURL, options);
    }
    return WebSocketManager.instance;
  }

  /**
   * 构造函数
   * @param {string} webSocketURL - WebSocket 连接地址
   * @param {Object} [options={}] - 配置选项
   */
  constructor(webSocketURL, options = {}) {
    if (WebSocketManager.instance) {
      return WebSocketManager.instance;
    }

    this.webSocketURL = webSocketURL;
    this.webSocket = null;
    this.isConnected = false;
    this.listeners = new Map();
    this.pollingTimers = new Map();
    this.reconnectTimer = null;
    this.reconnectInterval = 10000;  // 重连间隔，默认10秒
    this.maxReconnectAttempts = options.maxReconnectAttempts || 0;  // 最大重连次数，0表示无限重连
    this.reconnectAttempts = 0;      // 当前重连次数

    // 心跳检测配置
    this.heartbeatInterval = options.heartbeatInterval || 30000;  // 心跳间隔，默认30秒
    this.heartbeatTimer = null;

    WebSocketManager.instance = this;
  }

  /**
   * 初始化 WebSocket 连接
   */
  init() {
    if (this.webSocket) {
      this.close();
    }

    try {
      this.webSocket = new WebSocket(this.webSocketURL);
      this.initWebSocketEvents();
    } catch (error) {
      console.error('WebSocket 连接创建失败:', error);
      this.scheduleReconnect();
    }
  }

  /**
   * 初始化 WebSocket 事件监听
   * @private
   */
  initWebSocketEvents() {
    this.webSocket.onopen = (event) => {
      console.log('WebSocket 连接已建立');
      this.isConnected = true;
      this.reconnectAttempts = 0;
      this.startHeartbeat();
      this.emit('open', event);
    };

    this.webSocket.onclose = (event) => {
      console.log('WebSocket 连接已关闭');
      this.isConnected = false;
      this.stopHeartbeat();
      this.emit('close', event);
      this.scheduleReconnect();
    };

    this.webSocket.onerror = (error) => {
      console.error('WebSocket 错误:', error);
      this.isConnected = false;
      this.emit('error', error);
    };

    this.webSocket.onmessage = (event) => {
      try {
        let data = event.data
        if (typeof data === 'string') {
          try {
            data = JSON.parse(data)
          } catch (e) {
            // 如果解析失败，使用原始数据
            data = {
              type: 'DEFAULT_PUSH',
              content: event.data,
              timestamp: Date.now()
            }
          }
        }
        console.log('WebSocket 收到消息:', data)
        this.emit('message', data)
      } catch (error) {
        console.warn('消息处理异常:', error)
        this.emit('error', error)
      }
    };
  }

  /**
   * 开始心跳检测
   * @private
   */
  startHeartbeat() {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      if (this.isConnected) {
        this.sendMessage({ type: 'heartbeat', timestamp: Date.now() });
      }
    }, this.heartbeatInterval);
  }

  /**
   * 停止心跳检测
   * @private
   */
  stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  /**
   * 安排重连
   * @private
   */
  scheduleReconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    if (this.maxReconnectAttempts > 0 && this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log('达到最大重连次数，停止重连');
      this.emit('reconnectFailed');
      return;
    }

    this.reconnectTimer = setTimeout(() => {
      console.log(`尝试重连... (第 ${this.reconnectAttempts + 1} 次)`);
      this.reconnectAttempts++;
      this.init();
    }, this.reconnectInterval);
  }

  /**
   * 手动重连
   */
  reconnect() {
    this.reconnectAttempts = 0;
    this.scheduleReconnect();
  }

  /**
   * 发送消息
   * @param {Object} data - 要发送的数据
   */
  sendMessage(data) {
    if (!this.isConnected) {
      console.warn('WebSocket 未连接，消息发送失败');
      return;
    }

    try {
      const message = typeof data === 'string' ? data : JSON.stringify(data);
      this.webSocket.send(message);
    } catch (error) {
      console.error('消息发送失败:', error);
      this.emit('error', error);
    }
  }

  /**
   * 发送命令到服务器，支持可选的轮询配置
   * @param {string} command - 命令名称
   * @param {Object} data - 命令参数
   * @param {Object} [pollingConfig] - 轮询配置，如果不传则为单次发送
   * @param {number} [pollingConfig.interval] - 轮询间隔(毫秒)
   * @returns {string|null} - 如果启用轮询则返回轮询ID，否则返回null
   */
  sendCommand(command, data = {}, pollingConfig = null) {
    const message = {
      type: 'command',
      command: command,
      data: data,
      timestamp: Date.now()
    };
    
    this.sendMessage(message);

    if (pollingConfig && pollingConfig.interval) {
      const pollingId = `polling_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      const timer = setInterval(() => {
        if (this.isConnected) {
          this.sendMessage(message);
        }
      }, pollingConfig.interval);
      
      this.pollingTimers.set(pollingId, {
        timer,
        command,
        data,
        interval: pollingConfig.interval
      });
      
      return pollingId;
    }
    
    return null;
  }

  /**
   * 停止特定的轮询
   * @param {string} pollingId - 轮询ID
   */
  stopPolling(pollingId) {
    const polling = this.pollingTimers.get(pollingId);
    if (polling) {
      clearInterval(polling.timer);
      this.pollingTimers.delete(pollingId);
    }
  }

  /**
   * 停止所有轮询
   */
  stopAllPolling() {
    this.pollingTimers.forEach((polling, id) => {
      clearInterval(polling.timer);
    });
    this.pollingTimers.clear();
  }

  /**
   * 订阅事件
   * @param {string} event - 事件名称
   * @param {Function} callback - 回调函数
   */
  subscribe(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event).add(callback);
  }

  /**
   * 取消订阅事件
   * @param {string} event - 事件名称
   * @param {Function} callback - 回调函数
   */
  unsubscribe(event, callback) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).delete(callback);
    }
  }

  /**
   * 触发事件
   * @private
   * @param {string} event - 事件名称
   * @param {*} data - 事件数据
   */
  emit(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`事件处理错误 (${event}):`, error);
        }
      });
    }
  }

  /**
   * 关闭连接
   */
  close() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    this.stopHeartbeat();
    this.stopAllPolling();

    if (this.webSocket) {
      this.webSocket.close();
      this.webSocket = null;
    }
    
    this.isConnected = false;
  }
}

// 使用示例：
/*
const ws = WebSocketManager.getInstance('ws://example.com/websocket', {
    maxReconnectAttempts: 0,  // 设置为0表示无限重连
    heartbeatInterval: 30000  // 心跳间隔30秒
});

// 监听事件
ws.subscribe('open', () => {
    console.log('连接成功');
});

ws.subscribe('close', () => {
    console.log('连接关闭');
});

ws.subscribe('error', (error) => {
    console.log('连接错误:', error);
});

ws.subscribe('message', (data) => {
    console.log('收到消息:', data);
});

ws.subscribe('reconnectFailed', () => {
    console.log('重连失败，达到最大重试次数');
});

// 初始化连接
ws.init();

// 发送普通命令
ws.sendCommand('GET_USER_DATA', { userId: 123 });

// 发送需要轮询的命令
const pollingId = ws.sendCommand('GET_REAL_TIME_DATA', 
    { type: 'temperature' }, 
    { interval: 5000 }
);

// 停止特定轮询
// ws.stopPolling(pollingId);

// 停止所有轮询
// ws.stopAllPolling();

// 关闭连接
// ws.close();
*/